import React, { useState, useEffect } from 'react'
import { useToast, Box, Heading, Button, Alert, AlertTitle, AlertDescription, Tabs, TabList, Tab, TabPanels, TabPanel } from '@chakra-ui/react'
import { ArrowLeftIcon } from '@chakra-ui/icons'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useBlocker } from 'react-router'

import utils from '../../../utils/utils'
import api from '../../../api'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

import UnsavedModal from '../../includes/UnsavedModal'
import Editor from './Editor'
import PollModeEditor from './Poll'
import ReminderEditor from './Reminders'
import SettingsEditor from './Settings'

import { POLL_FIELDS } from '../../Poll'

function NewStage () {
  const [stage, setStage] = useState()
  const [stages, setStages] = useState([])
  const [dataTypes, setDataTypes] = useState([])
  const [sites, setSites] = useState([])
  const [error, setError] = useState()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [postSubmissionText, setPostSubmissionText] = useState('')
  const [showPostSubmissionEditor, setShowPostSubmissionEditor] = useState(false)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurringFrequency, setRecurringFrequency] = useState(0)
  const [recurringDuration, setRecurringDuration] = useState(0)
  const [adHoc, setAdHoc] = useState(false)
  const [reminders, setReminders] = useState([])
  const [availableToSites, setAvailableToSites] = useState()
  const [enabled, setEnabled] = useState(true)
  const [enableAfter, setEnableAfter] = useState()
  const [components, setComponents] = useState([])
  const [isPoll, setIsPoll] = useState(false)
  const [customLinks, setCustomLinks] = useState([])
  const [unsaved, setUnsaved] = useState(false)
  const toast = useToast()
  const navigate = useNavigate()
  const { id, stageId } = useParams()
  const { trial, updateTrial, team } = useStore()
  useTitle('Edit Stage', trial)
  const isEditing = !!stageId

  useEffect(() => {
    setUnsaved(true)
  }, [name, description, postSubmissionText, isRecurring, recurringFrequency, recurringDuration, adHoc, availableToSites, enabled, enableAfter, reminders, components, isPoll, customLinks])

  useEffect(() => {
    api.trials.getStages(id, d => setStages(d.stages))
    api.trials.getDataTypes(id, d => setDataTypes(d.dataTypes))
    api.sites.getForTrial(id, d => setSites(d.sites))
    if (stageId) {
      api.stages.get(stageId, s => {
        setName(s.name || '')
        setDescription(s.description || '')
        setPostSubmissionText(s.postSubmissionText || '')
        setComponents(s.components || [])
        setIsRecurring(s.isRecurring || false)
        setRecurringFrequency(s.recurringFrequency || 0)
        setRecurringDuration(s.recurringDuration || 0)
        setAdHoc(s.adHoc || false)
        setReminders(s.reminders || [])
        setAvailableToSites(s.availableToSites || null)
        setEnabled(s.enabled || false)
        setEnableAfter(s.enableAfter || null)
        setIsPoll(s.isPoll || false)
        setCustomLinks(s.customLinks || [])
        setStage(s)
        setShowPostSubmissionEditor(true)
        setTimeout(() => setUnsaved(false), 1000) // Delay to ensure other useEffects have run
      })
    }
    if (!stageId) {
      setShowPostSubmissionEditor(true)
      setTimeout(() => setUnsaved(false), 1000) // Delay to ensure other useEffects have run
    }
  }, [stageId])

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    unsaved &&
    currentLocation.pathname !== nextLocation.pathname
  )

  function create () {
    setError()
    const data = { name, description, postSubmissionText, isRecurring, recurringFrequency, recurringDuration, adHoc, availableToSites, enabled, enableAfter, reminders, components, isPoll, customLinks }
    if (isEditing) {
      api.stages.update(stageId, data, stage => {
        setUnsaved(false)
        toast({ title: 'Stage saved', status: 'success' })
      }, err => setError(err.message))
    } else {
      api.trials.createStage(trial._id, data, stage => {
        setUnsaved(false)
        updateTrial(trial._id, { stageCount: (trial.stageCount ?? 0) + 1 })
        const navTarget = stages?.length === 0 ? 'manage' : 'stages'
        toast({ title: 'Stage created', status: 'success' })
        setTimeout(() => navigate(`/trials/${trial._id}/${navTarget}`), 50) // Delay to ensure state is set
      }, err => setError(err.message))
    }
  }

  const warnings = []
  if (isPoll) {
    components?.forEach(c => {
      const dataType = dataTypes?.filter(d => d._id === c.dataType)[0]
      if (!Object.keys(POLL_FIELDS).includes(dataType?.type)) {
        if (c.type !== 'title' && c.type !== 'sectionDivider') {
          warnings.push(`The field "${(c.title || dataType?.name) ?? 'title or section'}" is not available in polls.`)
        }
      }
    })
    if (isRecurring) {
      warnings.push('Enabling poll mode for recurring stages will allow participants to submit to the same stage multiple times.')
    }
  }

  return (
    <>
      <UnsavedModal blocker={blocker} />

      <Button mb={5} as={Link} to={`/trials/${trial._id}/stages`} variant='link' colorScheme='primary'><ArrowLeftIcon mr={3} /> Back to stages</Button>
      <Heading as='h2' size='lg'>{isEditing ? `Edit stage ${stage?.name}` : 'Create a new stage'}</Heading>

      {warnings.length > 0 &&
        <Alert status='warning' variant='left-accent' mt={5} flexDirection='column' alignItems='start'>
          <Heading size='sm'>This stage has {warnings.length} issue{warnings.length > 1 && 's'}</Heading>
          <Box ml={5}>
            <ul>
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </Box>
        </Alert>}

      <Tabs mt={5}>
        <TabList>
          <Tab>Content</Tab>
          <Tab>Reminders</Tab>
          <Tab>Settings</Tab>
          {utils.hasFeature(team, 'pollMode') && <Tab>Poll mode</Tab>}
        </TabList>
        <TabPanels>
          <TabPanel>
            <Editor trial={trial} stage={stage} name={name} setName={setName} description={description} setDescription={setDescription} components={components} setComponents={setComponents} dataTypes={dataTypes} setDataTypes={setDataTypes} />
          </TabPanel>

          <TabPanel>
            <ReminderEditor reminders={reminders} setReminders={setReminders} />
          </TabPanel>

          <TabPanel>
            <SettingsEditor trial={trial} stages={stages} stage={stage} isRecurring={isRecurring} setIsRecurring={setIsRecurring} recurringFrequency={recurringFrequency} setRecurringFrequency={setRecurringFrequency} recurringDuration={recurringDuration} setRecurringDuration={setRecurringDuration} adHoc={adHoc} setAdHoc={setAdHoc} availableToSites={availableToSites} setAvailableToSites={setAvailableToSites} enabled={enabled} setEnabled={setEnabled} enableAfter={enableAfter} showPostSubmissionEditor={showPostSubmissionEditor} setShowPostSubmissionEditor={setShowPostSubmissionEditor} postSubmissionText={postSubmissionText} setPostSubmissionText={setPostSubmissionText} sites={sites} setEnableAfter={setEnableAfter} />
          </TabPanel>

          {utils.hasFeature(team, 'pollMode') &&
            <TabPanel>
              <PollModeEditor stage={stage} isPoll={isPoll} setIsPoll={setIsPoll} customLinks={customLinks} setCustomLinks={setCustomLinks} />
            </TabPanel>}
        </TabPanels>
      </Tabs>

      <Box mt={10} ml={10} mr={10} position='sticky' style={{ bottom: 10 }} boxShadow='lg' p={3} rounded='md' bg='primary.50'>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          {error
            ? (
              <Alert mr={3} status='error' variant='left-accent'>
                <Box>
                  <AlertTitle>There was a problem saving your stage</AlertTitle>
                  <AlertDescription>{error}. Please fix these issues and try again.</AlertDescription>
                </Box>
              </Alert>)
            : <Box />}
          <Button size='lg' colorScheme='primary' onClick={create}>{isEditing ? 'Save stage' : 'Create stage'}</Button>
        </Box>
      </Box>
    </>
  )
}

export default NewStage
